import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { MovementMilestone } from "@tih/shared";
import { fetchMovementMilestones } from "../api/client";
import { useAuth } from "../context/AuthContext";
import { useReveal } from "../hooks/useReveal";
import { CopyText } from "./CopyText";
import {
  TimelineMilestoneEditModal,
  emptyMilestoneDraft,
  milestoneHref,
  milestoneLinkLabel,
  milestoneToDraft,
} from "./TimelineMilestoneEditModal";
import { formatMilestoneDate } from "../lib/milestoneDate";

function sortMilestones(items: MovementMilestone[]): MovementMilestone[] {
  return [...items].sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id);
}

export function Movement() {
  const ref = useReveal<HTMLElement>();
  const { isAdmin } = useAuth();
  const [milestones, setMilestones] = useState<MovementMilestone[]>([]);
  const [editing, setEditing] = useState<ReturnType<typeof milestoneToDraft> | null>(null);

  const refresh = useCallback(async () => {
    try {
      const rows = await fetchMovementMilestones();
      setMilestones(sortMilestones(rows));
    } catch {
      setMilestones([]);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  function upsertMilestone(saved: MovementMilestone) {
    setMilestones((prev) => {
      const next = prev.some((m) => m.id === saved.id)
        ? prev.map((m) => (m.id === saved.id ? saved : m))
        : [...prev, saved];
      return sortMilestones(next);
    });
  }

  function removeMilestone(id: number) {
    setMilestones((prev) => prev.filter((m) => m.id !== id));
  }

  function openNewMilestone() {
    const maxSort = milestones.reduce((max, m) => Math.max(max, m.sortOrder), -1);
    setEditing(emptyMilestoneDraft(maxSort + 1));
  }

  return (
    <>
      <section className="section section-movement" id="movement" ref={ref}>
        <div className="sec-eyebrow center">
          <em>
            <CopyText path="movement.eyebrow" as="span" />
          </em>
        </div>
        <h2 className="sec-title center">
          <CopyText path="movement.titleLine1" as="span" />
          <br />
          <CopyText path="movement.titleLine2" as="span" />
        </h2>

        <div className="timeline">
          {milestones.map((m) => {
            const href = milestoneHref(m);
            const linkLabel = milestoneLinkLabel(m);
            return (
              <div className="tl-item" key={m.id}>
                {isAdmin && (
                  <button
                    type="button"
                    className="copy-timeline-edit"
                    aria-label={`Edit milestone: ${m.title}`}
                    title="Edit milestone (admin)"
                    onClick={() => setEditing(milestoneToDraft(m))}
                  >
                    ✎
                  </button>
                )}
                <div className="tl-year">{formatMilestoneDate(m.year)}</div>
                <div className="tl-body">
                  {href ? (
                    <h3>
                      <Link to={href}>{m.title}</Link>
                    </h3>
                  ) : (
                    <h3>{m.title}</h3>
                  )}
                  <p>{m.body}</p>
                  {href && linkLabel && (
                    <p className="tl-link">
                      <Link to={href}>{linkLabel} →</Link>
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {isAdmin && (
          <div className="timeline-admin-actions">
            <button
              type="button"
              className="btn btn-ghost"
              aria-haspopup="dialog"
              onClick={openNewMilestone}
            >
              + Add milestone
            </button>
          </div>
        )}
      </section>

      {editing && (
        <TimelineMilestoneEditModal
          open
          draft={editing}
          onClose={() => setEditing(null)}
          onSaved={upsertMilestone}
          onDeleted={removeMilestone}
        />
      )}
    </>
  );
}
